import { Grid, Typography } from "@mui/material";
import * as React from "react";
import { DialogSectionCard } from "source/components/framework/Dialog/DialogSectionCard";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { IIdTitleNode, IdTitleNode } from "ui/Scripts/source/models/common/idTitleNode";
import { IEventSettingSimple } from "ui/Scripts/source/models/eventSettings/eventSettingSimple";
import { Translations } from "ui/Scripts/source/models/translations";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { IEventEditItem } from "../../../models/event/eventEditItem";
import { IEventEditItemMessage } from "ui/Scripts/source/models/event/eventEditItemMessage";
import * as eventService from "../../../models/services/eventService";
import { UserListItems } from "../../../models/user/userListItems";
import { AttachmentList } from "../../attachments/AttachmentList";
import { LongText } from "../../framework/Form/LongText";
import { TextFieldWithButtonGroup } from "../../framework/Form/TextFieldWithButtonGroup";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { UserBlocks } from "../../user/UserBlocks/UserBlocks";
import { EventEditDialogMessageList } from "./EventEditDialogMessageList";

export interface IEventEditDialogActionProp {
    eventSetting: IEventSettingSimple;
    maxEventMessageAttachmentSize: number;
    companyId: string;
    unitId: string;
    unitCode: string;
    userCode: string;
    event: IEventEditItem;
    units: IIdTitleNode[];
    isClosed: boolean;
    userIsCreator: boolean;
    canHandleEvent: boolean;
    isLockedToOtherUser: boolean;
    lastApproveUser: IModifyUser;
    processingTimeStr: string;
    // Action, Comments
    immediateAction: string;
    preventiveAction: string;
    rootCause: string;
    handlers: IModifyUser[];
    attachments: IFileAttachment[];
    messages: IEventEditItemMessage[];
    onChange: (name: string, value: string) => void;
    onAddUsers: (users: IModifyUser[]) => Promise<ApiSuccess>;
    onRemoveUsers: (userCodes: string[]) => Promise<ApiSuccess>;
    onApprove: (userCode: string) => void;
    onRemoveAttachment: (id: string) => void;
    onDownloadAttachment: (id: string) => void;
    onAddAttachments: (fileList: FileList) => void;
    onEventMessageSaved: (newMessage: IEventEditItemMessage) => void;
    onEventMessageRead: (id: string) => void;
    onSelectUnit: () => void;
}

export class EventEditDialogAction extends React.Component<IEventEditDialogActionProp, {}> {
    handleChange = (event) => {
        const target = event.target;
        this.props.onChange(target.name, target.value);
    };

    // #region Handlers
    handleGetHandlersSelectListItems = (companyId: string, pageSize: number, page: number, filter: string,
        ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, _ignoreQrCodeUsers: boolean, _requireQrCodeUsers: boolean,
        doNotIgnoreRecordUserConnectedUserCode: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
        const props = this.props;
        return eventService.getHandlersSelectUserListItems(props.companyId, props.event.id, props.unitId, pageSize, page, filter,
            ignoreUserIds, ignoreUserCodes, activityState, ignoreRecordUserConnected, doNotIgnoreRecordUserConnectedUserCode,
            sortColumn, sortOrderIsAsc);
    };
    // #endregion Handlers
    
    render() {
        const props = this.props;
        const event = props.event;
        if (!event) return null;
        const isPublished = event.isPublished();
        const activeCompanyIsReceivingCompany = props.companyId === event.companyId;
        const handersAreEditable = !props.isClosed && activeCompanyIsReceivingCompany && props.canHandleEvent;
        const actionsAndRootCauseAreEditable = !props.isLockedToOtherUser && handersAreEditable;
        const messagesAreEditable = props.userIsCreator || props.canHandleEvent;
        const unitIsVisible = activeCompanyIsReceivingCompany && isPublished;
        const unitIsEditable = !props.isLockedToOtherUser && IdTitleNode.getFirstActiveNode(props.units) && props.canHandleEvent;
        const attachmentsAreEditable = activeCompanyIsReceivingCompany && props.canHandleEvent;
        return (
            <>
                <GridRow sx={{ height: "100%" }}>
                    <Grid item sm={12} md={4} sx={{ height: "calc(100% + 16px)" }}>
                        <DialogSectionCard headerTitle={Translations.ActionsAndRootCause} overflowY="auto">
                            <GridRow>
                                <Grid item xs>
                                    <LongText
                                        classes="form-group"
                                        disabled={!actionsAndRootCauseAreEditable}
                                        expandable={true}
                                        title={Translations.ImmediateAction}
                                        maxLength={10000}
                                        name="immediateAction"
                                        value={props.immediateAction}
                                        valueClasses="xmSize"
                                        marginTop={0}
                                        onChange={this.handleChange}
                                    />
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item xs>
                                    <LongText
                                        classes="form-group"
                                        disabled={!actionsAndRootCauseAreEditable}
                                        expandable={true}
                                        title={Translations.PreventiveAction}
                                        maxLength={10000}
                                        name="preventiveAction"
                                        value={props.preventiveAction}
                                        valueClasses="xmSize"
                                        onChange={this.handleChange}
                                    />
                                </Grid>
                            </GridRow>
                            <GridRow>
                                <Grid item xs>
                                    <LongText
                                        classes="form-group"
                                        disabled={!actionsAndRootCauseAreEditable}
                                        expandable={true}
                                        title={Translations.RootCause}
                                        maxLength={10000}
                                        name="rootCause"
                                        value={props.rootCause}
                                        valueClasses="xmSize"
                                        onChange={this.handleChange}
                                    />
                                </Grid>
                            </GridRow>
                        </DialogSectionCard>
                    </Grid>
                    <Grid item sm={12} md={4} sx={{ height: "calc(100% + 16px)" }}>
                        <DialogSectionCard headerTitle={Translations.Comments} overflowY="auto">
                            <EventEditDialogMessageList
                                classes="form-group"
                                maxEventMessageAttachmentSize={props.maxEventMessageAttachmentSize}
                                companyId={props.companyId}
                                event={props.event}
                                handlers={props.handlers}
                                messages={props.messages}
                                isReadOnly={!messagesAreEditable}
                                lateThreshold={props.lastApproveUser?.date}
                                onEventMessageSaved={props.onEventMessageSaved}
                                onEventMessageRead={props.onEventMessageRead}
                            />
                        </DialogSectionCard>
                    </Grid>
                    <Grid item sm={12} md={4} sx={{ height: "calc(100% + 16px)" }}>
                        <DialogSectionCard headerTitle={Translations.HandlersAndAttachments} overflowY="hidden">
                            <div>
                                {unitIsVisible &&
                                    <FormField title={Translations.Unit} margin={0}>
                                        <TextFieldWithButtonGroup
                                            type="text"
                                            title={Translations.Unit}
                                            value={props.unitCode}
                                            disabled={true}
                                            buttons={[
                                                { label: Translations.Select, onClick: props.onSelectUnit, hidden: !unitIsEditable }
                                            ]}
                                        />
                                    </FormField>
                                }
                                <UserBlocks
                                    companyId={props.companyId}
                                    userCode={props.userCode}
                                    isReadOnly={!handersAreEditable}
                                    title={Translations.Handlers}
                                    noUsersMessage={Translations.AddHandlers}
                                    parentIsNew={false}
                                    parentIsNewMessage={""}
                                    users={props.handlers}
                                    margin={unitIsVisible ? undefined : 0}
                                    maxHeight="calc(100vh / 5)"
                                    removeUsersConfirmation={Translations.DoYouReallyWantToRemoveSelectedHandlers}
                                    onAddUsers={props.onAddUsers}
                                    onRemoveUsers={props.onRemoveUsers}
                                    onGetUserSelectListItems={this.handleGetHandlersSelectListItems}
                                    onApprove={props.onApprove}
                                />
                                {!!props.processingTimeStr &&
                                    <GridRow>
                                        <Grid item xs>
                                            <FormField title={Translations.ProcessingTime}>
                                                <Typography>{props.processingTimeStr}</Typography>
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                }
                            </div>
                            <AttachmentList
                                size="sm"
                                imageSize={props.eventSetting.attachmentImageSize}
                                title={Translations.Attachments}
                                attachments={props.attachments.filter(i => i.category > 0.5)}
                                isReadOnly={!attachmentsAreEditable}
                                onRemoveAttachment={props.onRemoveAttachment}
                                onDownloadAttachment={props.onDownloadAttachment}
                                onAddAttachments={props.onAddAttachments}
                            />
                        </DialogSectionCard>
                    </Grid>
                </GridRow>
            </>
        );
    }
}